import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useClient } from '@/context/ClientContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function Reports() {
  const { activeClient } = useClient();
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState('Authority Brief');
  const [frequency, setFrequency] = useState('weekly');

  const load = async () => {
    if (!activeClient) return;
    const { data } = await api.get(`/clients/${activeClient.client_id}/reports`);
    setItems(data || []);
  };
  useEffect(() => { load(); }, [activeClient]);

  if (!activeClient) return <div className="text-sm text-muted-foreground">Select a client.</div>;

  const create = async () => {
    await api.post('/reports', { client_id: activeClient.client_id, title, frequency });
    await load();
    toast.success('Report generated');
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Reporting engine</div>
        <h1 className="mt-2 font-display text-3xl tracking-tight">Branded reports</h1>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="grid md:grid-cols-[1fr_180px_auto] gap-3">
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Report title" />
          <Select value={frequency} onValueChange={setFrequency}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={create}><FileText className="h-4 w-4 mr-1" /> Generate</Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No reports yet. Generate your first executive brief above.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(r => (
            <div key={r.report_id} className="rounded-xl border border-border bg-card p-5 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{r.frequency}</Badge>
                  <Badge variant="secondary">{r.status}</Badge>
                </div>
                <div className="mt-2 font-display text-lg">{r.title}</div>
                <div className="text-xs text-muted-foreground">
                  Score {r.snapshot?.authority_score} · Grade {r.snapshot?.letter_grade} · {r.snapshot?.certification}
                </div>
                <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {new Date(r.created_at).toLocaleString()}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => downloadReport(r)}><Download className="h-4 w-4 mr-1" /> Download</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function downloadReport(r) {
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${r.title}</title>
  <style>body{font-family:Inter,system-ui;margin:40px;color:#0a0a0a}h1{font-size:28px}h2{font-size:18px;margin-top:24px}.k{color:#6b7280;font-size:12px;text-transform:uppercase;letter-spacing:.1em}.s{font-size:48px;color:#0052ff;font-weight:600}</style>
  </head><body>
  <div class="k">AuthorityOS™ · Newsly.Global</div>
  <h1>${r.title}</h1>
  <div class="k">Company</div><div>${r.snapshot?.company_name || ''}</div>
  <h2>Authority Score™</h2>
  <div class="s">${r.snapshot?.authority_score} <span style="font-size:18px;color:#6b7280">/ ${r.snapshot?.letter_grade} · ${r.snapshot?.certification}</span></div>
  <h2>Sub-scores</h2>
  <table style="width:100%;border-collapse:collapse">
  ${Object.entries(r.snapshot?.scores || {}).map(([k,v]) => `<tr><td style="padding:6px;border-bottom:1px solid #eee">${k}</td><td style="padding:6px;border-bottom:1px solid #eee;text-align:right">${v}</td></tr>`).join('')}
  </table>
  <p style="margin-top:40px;color:#6b7280;font-size:12px">Generated ${new Date(r.created_at).toLocaleString()}</p>
  </body></html>`;
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${r.title.replace(/\s+/g, '-')}.html`; a.click();
  URL.revokeObjectURL(url);
}
